import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, FileText, Phone, Mail, Check, ChevronRight,
  Upload, Shield, QrCode, Download, Clock, AlertCircle, ArrowLeft, Link2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import DynamicFormRenderer, { useUTMParams } from "../components/DynamicFormRenderer";

import { API } from "../config/api";

const STEPS = {
  lead: ["details", "otp", "done"],
  eoi: ["details", "otp", "cheque", "done"],
};

export default function ChannelPartnerPortal() {
  const { partnerCode } = useParams();
  const [partner, setPartner] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Form mode: "choose" | "lead" | "eoi"
  const [mode, setMode] = useState("choose");
  const [step, setStep] = useState("details");
  const [sessionId] = useState(() => `cp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

  // Form fields
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    project_interest: "", project_id: "",
    configuration: "", budget_min: "", budget_max: "", locality: "", notes: "",
    token_amount: "", agreed_price: "", payment_mode: "Cheque",
    unit_type: "", floor_preference: "", facing: "", preference_notes: "",
  });

  // Agent details (from partner record)
  const [agentPhone, setAgentPhone] = useState("");
  const [agentEmail, setAgentEmail] = useState("");

  // OTP state
  const [customerOtp, setCustomerOtp] = useState({ id: "", code: "", verified: false, mockOtp: "", channel: "whatsapp" });
  const [agentOtp, setAgentOtp] = useState({ id: "", code: "", verified: false, mockOtp: "", channel: "whatsapp" });
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  // Cheque upload
  const [chequeFile, setChequeFile] = useState(null);
  const [chequeUrl, setChequeUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // UTM params from URL
  const utmParams = useUTMParams();

  // Submission result
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Duplicate EOI state
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/cp-portal/public/${partnerCode}`);
        setPartner(res.data);
        setProjects(res.data.projects || []);
        setAgentPhone(res.data.partner_phone || "");
        setAgentEmail(res.data.partner_email || "");
      } catch {
        setError("Invalid or expired partner link.");
      } finally {
        setLoading(false);
      }
    })();
  }, [partnerCode]);

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // ── OTP Handlers ────────────────────────────────────────────
  const sendOtp = async (recipientType) => {
    setOtpSending(true);
    try {
      const isCustomer = recipientType === "customer";
      const phone = isCustomer ? form.phone : agentPhone;
      const email = isCustomer ? form.email : agentEmail;
      const channel = isCustomer ? customerOtp.channel : agentOtp.channel;

      const res = await axios.post(`${API}/cp-portal/public/${partnerCode}/send-otp`, {
        phone, email, recipient_type: recipientType, channel, session_id: sessionId,
      });
      const d = res.data;
      if (isCustomer) {
        setCustomerOtp(prev => ({ ...prev, id: d.otp_id, mockOtp: d.mock_otp, verified: false }));
      } else {
        setAgentOtp(prev => ({ ...prev, id: d.otp_id, mockOtp: d.mock_otp, verified: false }));
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async (recipientType) => {
    setOtpVerifying(true);
    setError("");
    try {
      const isCustomer = recipientType === "customer";
      const otp = isCustomer ? customerOtp : agentOtp;
      await axios.post(`${API}/cp-portal/public/${partnerCode}/verify-otp`, {
        otp_id: otp.id, otp_code: otp.code, session_id: sessionId,
      });
      if (isCustomer) {
        setCustomerOtp(prev => ({ ...prev, verified: true }));
      } else {
        setAgentOtp(prev => ({ ...prev, verified: true }));
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  // ── Cheque Upload ───────────────────────────────────────────
  const uploadCheque = async () => {
    if (!chequeFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", chequeFile);
      fd.append("session_id", sessionId);
      const res = await axios.post(`${API}/cp-portal/public/${partnerCode}/upload-cheque`, fd);
      setChequeUrl(res.data.cheque_url || res.data.path);
    } catch (e) {
      setError("Cheque upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  // ── Submit ──────────────────────────────────────────────────
  const submitForm = async (forceCreate = false) => {
    setSubmitting(true);
    setError("");
    try {
      if (mode === "lead") {
        const res = await axios.post(`${API}/cp-portal/public/${partnerCode}/register-lead`, {
          session_id: sessionId,
          name: form.name, phone: form.phone, email: form.email,
          project_interest: form.project_interest,
          configuration: form.configuration,
          budget_min: form.budget_min || undefined,
          budget_max: form.budget_max || undefined,
          locality: form.locality, notes: form.notes,
        });
        setResult(res.data);
        setStep("done");
      } else {
        const res = await axios.post(`${API}/cp-portal/public/${partnerCode}/register-eoi`, {
          session_id: sessionId,
          name: form.name, phone: form.phone, email: form.email,
          project_id: form.project_id,
          token_amount: form.token_amount, agreed_price: form.agreed_price,
          payment_mode: form.payment_mode,
          cheque_url: chequeUrl,
          unit_type: form.unit_type, floor_preference: form.floor_preference,
          facing: form.facing, preference_notes: form.preference_notes,
          notes: form.notes,
          force_create: forceCreate,
        });
        
        // Check for duplicate warning
        if (res.data.duplicate_warning && !forceCreate) {
          setDuplicateWarning(res.data);
          setShowDuplicateDialog(true);
          setSubmitting(false);
          return;
        }
        
        setResult(res.data);
        setStep("done");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicateConfirm = () => {
    setShowDuplicateDialog(false);
    setDuplicateWarning(null);
    submitForm(true); // Force create
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateDialog(false);
    setDuplicateWarning(null);
  };

  const canProceedFromDetails = () => {
    if (!form.name.trim() || !form.phone.trim()) return false;
    if (mode === "eoi" && (!form.project_id || !form.token_amount || !form.agreed_price)) return false;
    return true;
  };

  const bothOtpsVerified = customerOtp.verified && agentOtp.verified;

  // ── Render ──────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading...</div>
    </div>
  );

  if (error && !partner) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-slate-900 border-slate-800">
        <CardContent className="p-10 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Link Not Found</h2>
          <p className="text-slate-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-800/60 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white font-['Manrope']">PropertyCRM</h1>
            <p className="text-xs text-slate-400">Partner: {partner?.partner_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Mode selection */}
        {mode === "choose" && (
          <div className="space-y-6" data-testid="cp-mode-selection">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Channel Partner Portal</h2>
              <p className="text-slate-400">Welcome, {partner?.partner_name}. What would you like to register?</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <button
                onClick={() => { setMode("lead"); setStep("details"); }}
                data-testid="cp-register-lead-btn"
                className="group bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-amber-500/40 rounded-xl p-6 text-left transition-all"
              >
                <Users className="w-10 h-10 text-blue-400 mb-3 group-hover:text-blue-300" />
                <h3 className="text-lg font-semibold text-white mb-1">Register Lead</h3>
                <p className="text-sm text-slate-400">Register a new customer lead with OTP verification</p>
              </button>
              <button
                onClick={() => { setMode("eoi"); setStep("details"); }}
                data-testid="cp-register-eoi-btn"
                className="group bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-amber-500/40 rounded-xl p-6 text-left transition-all"
              >
                <FileText className="w-10 h-10 text-amber-400 mb-3 group-hover:text-amber-300" />
                <h3 className="text-lg font-semibold text-white mb-1">Register EOI</h3>
                <p className="text-sm text-slate-400">Expression of Interest with cheque upload & digital token</p>
              </button>
            </div>
          </div>
        )}

        {/* Step indicator */}
        {mode !== "choose" && step !== "done" && (
          <div className="mb-8">
            <button onClick={() => { if (step === "details") { setMode("choose"); } else { const steps = STEPS[mode]; setStep(steps[steps.indexOf(step) - 1]); }}} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-4">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2">
              {STEPS[mode].filter(s => s !== "done").map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                    step === s ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" :
                    STEPS[mode].indexOf(step) > i ? "bg-green-500/10 text-green-400" : "text-slate-500"
                  }`}>
                    {STEPS[mode].indexOf(step) > i ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
                    <span className="capitalize">{s}</span>
                  </div>
                  {i < STEPS[mode].filter(s2 => s2 !== "done").length - 1 && <ChevronRight className="w-4 h-4 text-slate-600" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && partner && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-sm text-red-400 flex items-center gap-2" data-testid="cp-error-banner">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            <button onClick={() => setError("")} className="ml-auto text-red-300 hover:text-white">dismiss</button>
          </div>
        )}

        {/* Step: Details */}
        {step === "details" && mode !== "choose" && (
          <Card className="bg-slate-800/60 border-slate-700/50" data-testid="cp-details-form">
            <CardHeader>
              <CardTitle className="text-white text-lg">
                {mode === "lead" ? "Customer Details" : "EOI Details"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Customer Name *</label>
                  <Input data-testid="cp-customer-name" value={form.name} onChange={e => updateForm("name", e.target.value)} placeholder="Full name" className="bg-slate-900/60 border-slate-600 text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Customer Phone *</label>
                  <Input data-testid="cp-customer-phone" value={form.phone} onChange={e => updateForm("phone", e.target.value)} placeholder="+91 XXXXXXXXXX" className="bg-slate-900/60 border-slate-600 text-white" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Customer Email</label>
                <Input data-testid="cp-customer-email" value={form.email} onChange={e => updateForm("email", e.target.value)} placeholder="email@example.com" className="bg-slate-900/60 border-slate-600 text-white" />
              </div>

              {mode === "lead" && (
                <DynamicFormRenderer
                  formType="lead_capture"
                  source="cp_portal"
                  values={{...form, ...utmParams}}
                  onChange={(fieldName, value) => updateForm(fieldName, value)}
                  projects={projects}
                  excludeFields={["name", "phone", "email"]}
                  showGroups={false}
                  gridCols={2}
                  theme="dark"
                />
              )}

              {mode === "eoi" && (
                <>
                  {/* Project selection - kept separate for EOI flow control */}
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Project *</label>
                    <select data-testid="cp-eoi-project" value={form.project_id} onChange={e => { updateForm("project_id", e.target.value); const p = projects.find(pp => pp.project_id === e.target.value); if (p) updateForm("project_interest", p.name); }} className="w-full bg-slate-900/60 border border-slate-600 text-white rounded-md px-3 py-2 text-sm">
                      <option value="">Select project</option>
                      {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                    </select>
                  </div>
                  
                  {/* Dynamic EOI fields */}
                  <DynamicFormRenderer
                    formType="eoi"
                    source="cp_portal"
                    values={form}
                    onChange={(fieldName, value) => updateForm(fieldName, value)}
                    projects={projects}
                    excludeFields={["project_id", "lead_id"]}
                    showGroups={false}
                    gridCols={2}
                    theme="dark"
                  />
                </>
              )}

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => updateForm("notes", e.target.value)} placeholder="Additional notes..." rows={2} className="w-full bg-slate-900/60 border border-slate-600 text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>

              <Button
                data-testid="cp-proceed-to-otp"
                disabled={!canProceedFromDetails()}
                onClick={() => setStep("otp")}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white mt-4"
              >
                Proceed to OTP Verification <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: OTP Verification */}
        {step === "otp" && (
          <Card className="bg-slate-800/60 border-slate-700/50" data-testid="cp-otp-step">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-400" /> OTP Verification
              </CardTitle>
              <p className="text-sm text-slate-400">Both customer and agent must verify their identity</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer OTP */}
              <OtpBlock
                label="Customer"
                phone={form.phone}
                email={form.email}
                otp={customerOtp}
                setOtp={setCustomerOtp}
                onSend={() => sendOtp("customer")}
                onVerify={() => verifyOtp("customer")}
                sending={otpSending}
                verifying={otpVerifying}
                testIdPrefix="customer"
              />

              {/* Agent OTP */}
              <OtpBlock
                label="Agent (You)"
                phone={agentPhone}
                email={agentEmail}
                otp={agentOtp}
                setOtp={setAgentOtp}
                onSend={() => sendOtp("agent")}
                onVerify={() => verifyOtp("agent")}
                sending={otpSending}
                verifying={otpVerifying}
                testIdPrefix="agent"
              />

              <Button
                data-testid="cp-otp-proceed"
                disabled={!bothOtpsVerified}
                onClick={() => {
                  if (mode === "eoi") {
                    setStep("cheque");
                  } else {
                    submitForm();
                  }
                }}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              >
                {bothOtpsVerified ? (mode === "eoi" ? "Proceed to Cheque Upload" : "Submit Lead Registration") : "Verify both OTPs to continue"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Cheque Upload (EOI only) */}
        {step === "cheque" && mode === "eoi" && (
          <Card className="bg-slate-800/60 border-slate-700/50" data-testid="cp-cheque-step">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Upload className="w-5 h-5 text-amber-400" /> Upload Cheque Copy
              </CardTitle>
              <p className="text-sm text-slate-400">Upload a photo or scan of the cheque for token amount</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  chequeFile ? "border-green-500/50 bg-green-500/5" : "border-slate-600 hover:border-amber-500/50 hover:bg-slate-800/40"
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  data-testid="cp-cheque-file-input"
                  onChange={e => { setChequeFile(e.target.files[0] || null); setChequeUrl(""); }}
                />
                {chequeFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <Check className="w-10 h-10 text-green-400" />
                    <p className="text-green-400 font-medium">{chequeFile.name}</p>
                    <p className="text-xs text-slate-400">{(chequeFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-10 h-10 text-slate-500" />
                    <p className="text-slate-400">Click to upload cheque image</p>
                    <p className="text-xs text-slate-500">JPEG, PNG, PDF - Max 10MB</p>
                  </div>
                )}
              </div>

              {chequeFile && !chequeUrl && (
                <Button data-testid="cp-upload-cheque-btn" onClick={uploadCheque} disabled={uploading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {uploading ? "Uploading..." : "Upload Cheque"}
                </Button>
              )}

              {chequeUrl && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 flex items-center gap-2 text-sm text-green-400">
                  <Check className="w-4 h-4" /> Cheque uploaded successfully
                </div>
              )}

              <Button
                data-testid="cp-submit-eoi"
                disabled={!chequeUrl || submitting}
                onClick={submitForm}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              >
                {submitting ? "Submitting..." : "Submit EOI & Generate Token"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Done */}
        {step === "done" && result && (
          <div className="space-y-6" data-testid="cp-success-screen">
            <Card className="bg-slate-800/60 border-slate-700/50 border-green-500/20">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {mode === "lead" ? "Lead Registered!" : "EOI Submitted!"}
                </h2>
                <p className="text-slate-400 mb-4">
                  {mode === "lead"
                    ? "The lead has been successfully registered in the CRM."
                    : "The EOI has been registered and a digital launch token has been generated."}
                </p>
                {result.lead_id && (
                  <Badge className="bg-slate-700 text-slate-300 mb-2">Lead ID: {result.lead_id}</Badge>
                )}
                {result.eoi_id && (
                  <Badge className="bg-amber-500/20 text-amber-400 ml-2">EOI: {result.eoi_id}</Badge>
                )}
                {result.priority_number && (
                  <Badge className="bg-blue-500/20 text-blue-400 ml-2">Priority #{result.priority_number}</Badge>
                )}
              </CardContent>
            </Card>

            {/* Digital Token Card */}
            {result.digital_token && (
              <Card className="bg-slate-800/60 border-slate-700/50" data-testid="cp-digital-token-card">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-amber-400" /> Digital Launch Token
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 border border-slate-700/50">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Token Code</p>
                        <p className="text-2xl font-bold text-amber-400 font-mono">{result.digital_token.token_code}</p>
                      </div>
                      <Badge className="bg-amber-500/20 text-amber-400">Priority #{result.digital_token.priority_number}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Customer</p>
                        <p className="text-white">{result.digital_token.customer_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Project</p>
                        <p className="text-white">{result.digital_token.project_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Token Amount</p>
                        <p className="text-white">Rs. {Number(result.digital_token.token_amount).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Partner</p>
                        <p className="text-white">{result.digital_token.partner_name}</p>
                      </div>
                    </div>
                  </div>

                  {result.digital_token.token_image_url && (
                    <div className="text-center">
                      <img
                        src={result.digital_token.token_image_url}
                        alt="Digital Token"
                        className="max-w-xs mx-auto rounded-lg shadow-lg border border-slate-700"
                        data-testid="cp-token-image"
                      />
                      <a
                        href={result.digital_token.token_image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
                        data-testid="cp-download-token"
                      >
                        <Download className="w-4 h-4" /> Download Token
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Button
              data-testid="cp-register-another"
              onClick={() => {
                setMode("choose");
                setStep("details");
                setForm({ name: "", phone: "", email: "", project_interest: "", project_id: "", configuration: "", budget_min: "", budget_max: "", locality: "", notes: "", token_amount: "", agreed_price: "", payment_mode: "Cheque", unit_type: "", floor_preference: "", facing: "", preference_notes: "" });
                setCustomerOtp({ id: "", code: "", verified: false, mockOtp: "", channel: "whatsapp" });
                setAgentOtp({ id: "", code: "", verified: false, mockOtp: "", channel: "whatsapp" });
                setChequeFile(null);
                setChequeUrl("");
                setResult(null);
                setError("");
              }}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Register Another
            </Button>
          </div>
        )}
      </div>

      {/* Duplicate EOI Warning Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertCircle className="w-5 h-5" />
              Existing EOI Found
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {duplicateWarning?.message}
            </DialogDescription>
          </DialogHeader>
          
          {duplicateWarning?.existing_eois && (
            <div className="space-y-2 py-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Existing EOIs for {duplicateWarning.customer_name}</p>
              {duplicateWarning.existing_eois.map((eoi) => (
                <div key={eoi.eoi_id} className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white">{eoi.project_name || 'Project'}</span>
                    <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Active</Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                    <span>Token: Rs. {Number(eoi.token_amount).toLocaleString()}</span>
                    {eoi.created_at && (
                      <span>{new Date(eoi.created_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-4">
                <div className="flex items-start gap-2">
                  <Link2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-300">
                    If you proceed, the new EOI will be linked to the existing EOI(s) for tracking purposes.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleDuplicateCancel}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="cp-duplicate-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDuplicateConfirm}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="cp-duplicate-confirm"
            >
              Create Anyway & Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-slate-600">
        Powered by PropertyCRM
      </div>
    </div>
  );
}


// ── OTP Block Sub-component ─────────────────────────────────────
function OtpBlock({ label, phone, email, otp, setOtp, onSend, onVerify, sending, verifying, testIdPrefix }) {
  if (otp.verified) {
    return (
      <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4" data-testid={`${testIdPrefix}-otp-verified`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-green-400">{label} Verified</p>
            <p className="text-xs text-slate-400">{phone || email}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-4 space-y-3" data-testid={`${testIdPrefix}-otp-block`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white">{label} OTP</h4>
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => setOtp(prev => ({ ...prev, channel: "whatsapp" }))}
            className={`px-2 py-1 rounded ${otp.channel === "whatsapp" ? "bg-green-500/20 text-green-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`${testIdPrefix}-channel-whatsapp`}
          >
            <Phone className="w-3 h-3 inline mr-1" />WhatsApp
          </button>
          <button
            onClick={() => setOtp(prev => ({ ...prev, channel: "email" }))}
            className={`px-2 py-1 rounded ${otp.channel === "email" ? "bg-blue-500/20 text-blue-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`${testIdPrefix}-channel-email`}
          >
            <Mail className="w-3 h-3 inline mr-1" />Email
          </button>
        </div>
      </div>

      {!otp.id ? (
        <Button
          data-testid={`${testIdPrefix}-send-otp`}
          onClick={onSend}
          disabled={sending}
          size="sm"
          className="w-full bg-slate-700 hover:bg-slate-600 text-white"
        >
          {sending ? "Sending..." : `Send OTP to ${label}`}
        </Button>
      ) : (
        <div className="space-y-2">
          {/* MOCKED: Show the OTP for demo */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 text-center">
            <p className="text-[10px] text-amber-500/70 uppercase tracking-wider">Demo Mode - OTP</p>
            <p className="text-lg font-mono font-bold text-amber-400" data-testid={`${testIdPrefix}-mock-otp`}>{otp.mockOtp}</p>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={`${testIdPrefix}-otp-input`}
              value={otp.code}
              onChange={e => setOtp(prev => ({ ...prev, code: e.target.value }))}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              className="bg-slate-900/60 border-slate-600 text-white text-center tracking-widest font-mono"
            />
            <Button
              data-testid={`${testIdPrefix}-verify-otp`}
              onClick={onVerify}
              disabled={verifying || otp.code.length !== 6}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white px-6 shrink-0"
            >
              {verifying ? "..." : "Verify"}
            </Button>
          </div>
          <button onClick={onSend} className="text-xs text-slate-500 hover:text-slate-300" data-testid={`${testIdPrefix}-resend-otp`}>
            <Clock className="w-3 h-3 inline mr-1" />Resend OTP
          </button>
        </div>
      )}
    </div>
  );
}
